import { Link } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { Alert, AppLayout, Button, Card, RequireSession } from '@jmvilaca-corp/auth-ui'
import CalendarSyncIcon from '../components/CalendarSyncIcon'
import ConnectorBanner from '../components/ConnectorBanner'
import EventDialog, { type EventDialogTarget } from '../components/EventDialog'
import {
  canEditEvents,
  roleLabels,
  useCalendars,
  useEventTypes,
  useEvents,
  type CalendarEvent,
  type CalendarSummary,
} from '../lib/calendars'
import { useCalendarSync, type CalendarLinkState } from '../lib/calendarSync'
import { standardSource } from '../lib/connectors'
import { eventRank, layoutDay } from '../lib/dayLayout'
import {
  addDays,
  addMonths,
  dayKey,
  formatLongDay,
  formatMonth,
  formatWeek,
  monthGrid,
  occursOn,
  startOfDay,
  syncWindows,
  timeKey,
  weekDays,
} from '../lib/dates'

type View = 'month' | 'week'

const weekdayLabels = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.']
const monthChipLimit = 3

/**
 * Compte les changements de largeur d'un element, une fois la largeur stabilisee.
 *
 * Sert de `key` a la grille. Quand un iPhone pivote, Safari garde aux lignes de la
 * grille la hauteur calculee pour une largeur de passage : les etiquettes debordent
 * sur la semaine suivante en paysage, les semaines restent etirees en portrait.
 * Reconstruire la grille l'oblige a refaire le calcul.
 */
function useWidthChanges() {
  const [element, setElement] = useState<HTMLElement | null>(null)
  const [changes, setChanges] = useState(0)

  useEffect(() => {
    if (element === null || typeof ResizeObserver === 'undefined') {
      return
    }

    let width = element.clientWidth
    let timer: number | undefined
    const observer = new ResizeObserver(() => {
      if (element.clientWidth === width) {
        return
      }

      width = element.clientWidth
      window.clearTimeout(timer)
      timer = window.setTimeout(() => setChanges((current) => current + 1), 150)
    })

    observer.observe(element)
    return () => {
      observer.disconnect()
      window.clearTimeout(timer)
    }
  }, [element])

  return [setElement, changes] as const
}

export default function CalendarPage() {
  return (
    <RequireSession>
      {(user) => (
        <AppLayout user={user} wide>
          <CalendarBoard />
        </AppLayout>
      )}
    </RequireSession>
  )
}

export function CalendarBoard() {
  const [view, setView] = useState<View>('month')
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()))
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set())
  const [target, setTarget] = useState<EventDialogTarget | null>(null)

  const days = useMemo(() => (view === 'month' ? monthGrid(anchor) : weekDays(anchor)), [view, anchor])
  // Un jour de marge de chaque cote : un evenement sur la journee est porte en
  // UTC, et peut tomber juste hors de la grille exprimee en heure locale.
  const from = addDays(days[0]!, -1).toISOString()
  const to = addDays(days[days.length - 1]!, 2).toISOString()

  const calendars = useCalendars()
  const eventTypes = useEventTypes()
  const events = useEvents(from, to)

  // Ce qui est en base s'affiche tout de suite ; les calendriers lies se
  // mettent a jour derriere, la periode affichee d'abord.
  const windows = useMemo(() => syncWindows(view, anchor), [view, anchor])
  const linkStates = useCalendarSync(calendars.data ?? [], windows)
  const [observeGrid, gridWidthChanges] = useWidthChanges()

  if (calendars.isPending || eventTypes.isPending) {
    return <p className="text-sm text-neutral-500">Chargement...</p>
  }

  if (calendars.isError || eventTypes.isError) {
    return <Alert>{(calendars.error ?? eventTypes.error)?.message}</Alert>
  }

  if (calendars.data.length === 0) {
    return (
      <Card title="Bienvenue">
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          Vous n'avez pas encore de calendrier. Creez-en un pour y noter vos evenements ; vous pourrez ensuite le
          partager, en lecture seule ou en controle total.
        </p>
        <Link
          to="/calendars"
          className="mt-4 inline-block rounded-lg bg-neutral-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-neutral-800 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-white"
        >
          Creer mon premier calendrier
        </Link>
      </Card>
    )
  }

  const byId = new Map(calendars.data.map((calendar) => [calendar.calendarId, calendar]))
  // Un calendrier lie qui n'a pas pu se connecter garde ses evenements a
  // l'ecran, tels qu'a sa derniere mise a jour : son icone dit qu'ils datent.
  const visible = (events.data ?? []).filter((event) => byId.has(event.calendarId) && !hidden.has(event.calendarId))
  // Un evenement ne s'ajoute que dans un calendrier coche : cree dans un
  // calendrier masque, il n'apparaitrait pas dans la grille.
  const shown = calendars.data.filter((calendar) => !hidden.has(calendar.calendarId))
  const canCreate = shown.some(canEditEvents)
  const today = dayKey(new Date())
  // Les connecteurs des calendriers dont l'utilisateur est proprietaire : c'est
  // son compte qui y est lie, pas celui des personnes avec qui il les partage.
  const connectors = [
    ...new Set(
      calendars.data
        .filter((calendar) => calendar.role === 'owner' && calendar.source !== standardSource)
        .map((calendar) => calendar.source),
    ),
  ]

  const move = (direction: -1 | 1) =>
    setAnchor((current) => (view === 'month' ? addMonths(current, direction) : addDays(current, 7 * direction)))

  const toggle = (calendarId: string) =>
    setHidden((current) => {
      const next = new Set(current)
      if (!next.delete(calendarId)) {
        next.add(calendarId)
      }
      return next
    })

  return (
    <div className={`space-y-4 ${canCreate ? 'pb-20 sm:pb-0' : ''}`}>
      {connectors.map((connector) => (
        <ConnectorBanner key={connector} connector={connector} />
      ))}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => move(-1)} aria-label="Periode precedente">
            ‹
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setAnchor(startOfDay(new Date()))}>
            Aujourd'hui
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => move(1)} aria-label="Periode suivante">
            ›
          </Button>
          <h1 className="ml-2 text-xl font-semibold tracking-tight text-neutral-900 first-letter:uppercase dark:text-neutral-50">
            {view === 'month' ? formatMonth(anchor) : formatWeek(anchor)}
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <div role="group" aria-label="Affichage" className="flex rounded-lg border border-neutral-300 p-0.5 dark:border-neutral-700">
            {(['month', 'week'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={view === option}
                onClick={() => setView(option)}
                className={`rounded-md px-3 py-1 text-sm font-medium transition ${
                  view === option
                    ? 'bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900'
                    : 'text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
                }`}
              >
                {option === 'month' ? 'Mois' : 'Semaine'}
              </button>
            ))}
          </div>
          {/* Un seul bouton : rond et flottant sur un telephone, dans la barre a partir de `sm`. */}
          {canCreate && (
            <button
              type="button"
              onClick={() => setTarget({ day: dayKey(anchor) })}
              className="fixed bottom-5 right-5 z-10 flex h-14 w-14 items-center justify-center rounded-full bg-neutral-900 text-sm font-medium text-white shadow-lg transition hover:bg-neutral-800 active:scale-95 sm:static sm:h-auto sm:w-auto sm:rounded-lg sm:px-3 sm:py-1.5 sm:shadow-none sm:active:scale-100 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-white"
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="h-7 w-7 sm:hidden"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              <span className="sr-only sm:not-sr-only">Nouvel evenement</span>
            </button>
          )}
        </div>
      </div>

      {events.isError && <Alert>{events.error.message}</Alert>}

      <div className="grid gap-4 lg:grid-cols-[12rem_minmax(0,1fr)]">
        <CalendarList calendars={calendars.data} hidden={hidden} linkStates={linkStates} onToggle={toggle} />

        <div
          ref={observeGrid}
          className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900"
        >
          <div
            className={`grid-cols-7 border-b border-neutral-200 text-center text-xs font-medium uppercase tracking-wide text-neutral-500 dark:border-neutral-800 ${
              view === 'month' ? 'grid' : 'hidden sm:grid'
            }`}
          >
            {weekdayLabels.map((label) => (
              <div key={label} className="py-2">
                {label}
              </div>
            ))}
          </div>
          {/* Sur un telephone, sept colonnes ne laissent pas lire une semaine : elle devient une liste de jours. */}
          <div
            key={gridWidthChanges}
            className={`grid ${view === 'month' ? 'grid-cols-7' : 'grid-cols-1 sm:grid-cols-7'}`}
          >
            {days.map((day) => {
              const key = dayKey(day)
              return (
                <DayCell
                  key={key}
                  day={day}
                  view={view}
                  isToday={key === today}
                  isOutside={view === 'month' && day.getMonth() !== anchor.getMonth()}
                  events={visible.filter((event) => occursOn(event, key))}
                  calendars={byId}
                  onCreate={canCreate ? () => setTarget({ day: key }) : null}
                  onOpen={(event) => setTarget({ event })}
                  onShowDay={() => {
                    setAnchor(day)
                    setView('week')
                  }}
                />
              )
            })}
          </div>
        </div>
      </div>

      {target !== null && (
        <EventDialog
          target={target}
          calendars={shown}
          eventTypes={eventTypes.data}
          onClose={() => setTarget(null)}
        />
      )}
    </div>
  )
}

function CalendarList({
  calendars,
  hidden,
  linkStates,
  onToggle,
}: {
  calendars: CalendarSummary[]
  hidden: ReadonlySet<string>
  linkStates: ReadonlyMap<string, CalendarLinkState>
  onToggle: (calendarId: string) => void
}) {
  const mine = calendars.filter((calendar) => calendar.role === 'owner')
  const shared = calendars.filter((calendar) => calendar.role !== 'owner')

  const group = (title: string, items: CalendarSummary[]) =>
    items.length > 0 && (
      <div>
        <h2 className="text-xs font-medium uppercase tracking-wide text-neutral-500">{title}</h2>
        <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 lg:flex-col">
          {items.map((calendar) => {
            const linkState = linkStates.get(calendar.calendarId)
            // Grise, mais toujours affichable ou masquable : ses evenements restent a l'ecran.
            const disconnected = linkState === 'disconnected'

            return (
              <li key={calendar.calendarId} className="min-w-0">
                <label
                  className={`flex cursor-pointer items-start gap-2 text-sm ${
                    disconnected ? 'text-neutral-400 dark:text-neutral-600' : 'text-neutral-800 dark:text-neutral-200'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    style={{ accentColor: calendar.color }}
                    checked={!hidden.has(calendar.calendarId)}
                    onChange={() => onToggle(calendar.calendarId)}
                  />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate">{calendar.name}</span>
                      {linkState !== undefined && <CalendarSyncIcon state={linkState} />}
                    </span>
                    {calendar.role !== 'owner' && (
                      <span className="block truncate text-xs text-neutral-500">
                        {calendar.ownerName ?? calendar.ownerEmail ?? 'Proprietaire inconnu'} · {roleLabels[calendar.role]}
                      </span>
                    )}
                  </span>
                </label>
              </li>
            )
          })}
        </ul>
      </div>
    )

  return (
    <aside className="space-y-4" aria-label="Calendriers affiches">
      {group('Mes calendriers', mine)}
      {group('Partages avec moi', shared)}
      <Link
        to="/calendars"
        className="inline-block text-sm font-medium text-neutral-900 underline underline-offset-4 dark:text-neutral-100"
      >
        Gerer les calendriers
      </Link>
    </aside>
  )
}

function DayCell({
  day,
  view,
  isToday,
  isOutside,
  events,
  calendars,
  onCreate,
  onOpen,
  onShowDay,
}: {
  day: Date
  view: View
  isToday: boolean
  isOutside: boolean
  events: CalendarEvent[]
  calendars: Map<string, CalendarSummary>
  onCreate: (() => void) | null
  onOpen: (event: CalendarEvent) => void
  onShowDay: () => void
}) {
  const month = view === 'month'
  const { shown, more } = layoutDay(
    events,
    (event) => eventRank(event, (calendars.get(event.calendarId)?.source ?? standardSource) !== standardSource),
    month ? monthChipLimit : null,
  )

  return (
    <div
      // `min-w-0` : sans lui un mot long elargit sa colonne aux depens des autres.
      className={`min-w-0 border-b border-r border-neutral-200 dark:border-neutral-800 ${
        month ? 'min-h-24 p-0.5 sm:min-h-28 sm:p-1.5' : 'flex gap-2 p-1.5 sm:block sm:min-h-96'
      } ${isOutside ? 'bg-neutral-50 dark:bg-neutral-950/60' : ''} ${onCreate === null ? '' : 'cursor-pointer'}`}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onCreate?.()
        }
      }}
    >
      <button
        type="button"
        disabled={onCreate === null}
        onClick={() => onCreate?.()}
        aria-label={`${onCreate === null ? '' : 'Ajouter un evenement le '}${formatLongDay(day)}`}
        className={`flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full px-1 text-xs font-medium ${
          month ? '' : 'w-14 sm:w-auto'
        } ${
          isToday
            ? 'bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900'
            : isOutside
              ? 'text-neutral-400 dark:text-neutral-600'
              : 'text-neutral-700 dark:text-neutral-300'
        } ${onCreate === null ? '' : 'hover:ring-2 hover:ring-neutral-900/20 dark:hover:ring-neutral-100/30'}`}
      >
        {!month && <span className="mr-1 sm:hidden">{weekdayLabels[(day.getDay() + 6) % 7]}</span>}
        {day.getDate()}
      </button>

      <ul className={`space-y-1 ${month ? 'mt-1' : 'min-w-0 flex-1 sm:mt-1'}`}>
        {shown.map((event) => (
          <li key={event.eventId}>
            <EventChip
              event={event}
              color={calendars.get(event.calendarId)?.color ?? '#737373'}
              dense={month}
              onOpen={onOpen}
            />
          </li>
        ))}
      </ul>

      {more > 0 && (
        <button
          type="button"
          onClick={onShowDay}
          aria-label={`${more} autre${more > 1 ? 's' : ''} evenement${more > 1 ? 's' : ''}, voir la semaine`}
          className="mt-1 w-full text-center text-xs font-medium text-neutral-600 underline-offset-2 hover:underline sm:w-auto sm:text-left dark:text-neutral-400"
        >
          + {more}
          <span className="hidden sm:inline">
            {' '}
            autre{more > 1 ? 's' : ''}
          </span>
        </button>
      )}
    </div>
  )
}

function EventChip({
  event,
  color,
  dense,
  onOpen,
}: {
  event: CalendarEvent
  color: string
  /** Vue du mois : sur un telephone la case est tres etroite, l'heure cede sa place au titre. */
  dense: boolean
  onOpen: (event: CalendarEvent) => void
}) {
  const canceled = event.canceledAt !== null
  // Calendrier lie : ce qui ne concerne pas son compte est grise, ce qui le
  // concernera sans doute est hachure. Les deux s'ouvrent comme les autres.
  const aside = event.participation === 'other'
  const expected = event.participation === 'expected'
  const prefix = canceled ? 'Annule' : expected ? 'Convocation attendue' : aside ? 'Sans convocation' : null

  return (
    <button
      type="button"
      onClick={() => onOpen(event)}
      title={prefix === null ? event.title : `${prefix} : ${event.title}`}
      className={`block w-full overflow-hidden rounded py-0.5 text-left transition hover:brightness-95 ${
        aside
          ? 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800/70 dark:text-neutral-500'
          : 'text-neutral-900 dark:text-neutral-100'
      } ${expected ? 'event-chip-expected' : ''} ${
        dense ? 'px-0.5 text-[0.625rem] leading-3 sm:px-1.5 sm:text-xs' : 'px-1.5 text-sm sm:text-xs'
      } ${canceled ? 'line-through opacity-60' : ''}`}
      // Fond : la couleur du calendrier, a 20 % d'opacite (suffixe alpha hexadecimal),
      // 40 % sous les hachures qui en couvrent une partie. Grise : il n'en reste que le lisere.
      style={
        aside
          ? { borderLeft: `3px solid ${color}66` }
          : { backgroundColor: `${color}${expected ? '66' : '33'}`, borderLeft: `3px solid ${color}` }
      }
    >
      {/* Deux lignes au plus : une case de jour est etroite, et un titre coupe apres
          quelques lettres ne dit rien. La coupe est sur un span, pas sur le bouton :
          Safari ne la respecte pas sur un bouton, et l'etiquette deborde de sa case. */}
      <span className="line-clamp-2 break-words">
        {prefix !== null && <span className="sr-only">{prefix} : </span>}
        {!event.isAllDay && (
          <span className={`tabular-nums text-neutral-600 dark:text-neutral-400 ${dense ? 'hidden sm:inline' : ''}`}>
            {timeKey(new Date(event.startsAt))}{' '}
          </span>
        )}
        {/* Une espace, pas une marge : sans elle l'heure, le score et le premier mot
            du titre ne font qu'un mot, que le navigateur coupe au milieu. */}
        {event.score !== null && (
          <>
            <ScoreBadge score={event.score} />{' '}
          </>
        )}
        {event.title}
      </span>
    </button>
  )
}

/** Score d'un match joue : vert pour une victoire de l'equipe du calendrier, rouge pour une defaite. */
function ScoreBadge({ score }: { score: NonNullable<CalendarEvent['score']> }) {
  const { home, outside, isHome } = score
  const difference = isHome === null ? 0 : isHome ? home - outside : outside - home
  const outcome = isHome === null ? '' : difference > 0 ? ', victoire' : difference < 0 ? ', defaite' : ', match nul'
  const tone =
    difference > 0
      ? 'bg-green-600 text-white'
      : difference < 0
        ? 'bg-red-600 text-white'
        : 'bg-neutral-600 text-white dark:bg-neutral-300 dark:text-neutral-900'

  return (
    <span className={`rounded px-1 font-semibold whitespace-nowrap tabular-nums ${tone}`}>
      <span className="sr-only">
        Score {home} a {outside}
        {outcome}.
      </span>
      <span aria-hidden="true">
        {home}–{outside}
      </span>
    </span>
  )
}
